import { useState, useEffect } from 'react';
import {
  Truck, Save, ShieldCheck, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Loader2, Info, Link2, Search, FileSpreadsheet, Upload, ListChecks,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import API from '@/lib/api';
import { toast } from 'sonner';

function formatDT(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('es-ES')} · ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
}

export default function AjustesGLS() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [remitenteDraft, setRemitenteDraft] = useState({
    nombre: '', direccion: '', poblacion: '', provincia: '',
    cp: '', telefono: '', pais: '34',
  });
  const [pollingDraft, setPollingDraft] = useState(4);
  const [servicioDraft, setServicioDraft] = useState('1');
  const [horarioDraft, setHorarioDraft] = useState('3');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await API.get('/logistica/config/gls');
      setConfig(data);
      setRemitenteDraft(data.remitente);
      setPollingDraft(data.polling_hours);
      setServicioDraft(data.servicio_defecto || '1');
      setHorarioDraft(data.horario_defecto || '3');
    } catch {
      toast.error('Error cargando configuración GLS');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRemitente = async () => {
    setSaving(true);
    try {
      const { data } = await API.post('/logistica/config/gls/remitente', remitenteDraft);
      setConfig(data);
      setRemitenteDraft(data.remitente);
      toast.success('Remitente guardado en BD');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando remitente');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePolling = async () => {
    setSaving(true);
    try {
      const { data } = await API.post('/logistica/config/gls/polling', {
        polling_hours: parseFloat(pollingDraft),
      });
      setConfig(data);
      setPollingDraft(data.polling_hours);
      toast.success(`Intervalo actualizado a ${data.polling_hours}h`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando polling');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveServicio = async () => {
    setSaving(true);
    try {
      const { data } = await API.post('/logistica/config/gls/servicio', {
        servicio_defecto: servicioDraft,
        horario_defecto: horarioDraft,
      });
      setConfig(data);
      setServicioDraft(data.servicio_defecto);
      setHorarioDraft(data.horario_defecto);
      toast.success('Servicio y horario por defecto actualizados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando servicio');
    } finally {
      setSaving(false);
    }
  };

  const handleForcePoll = async () => {
    setForcing(true);
    try {
      const { data } = await API.post('/logistica/panel/actualizar-todos');
      toast.success(
        `Polling forzado. Procesados: ${data.procesados} · Cambios: ${data.cambios_estado}` +
        (data.preview ? ' (PREVIEW)' : ''),
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error forzando polling');
    } finally {
      setForcing(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await API.post('/logistica/config/gls/verify');
      if (data.ok) {
        toast.success(data.mensaje);
      } else {
        toast.error(data.mensaje + (data.detalle ? ` · ${data.detalle}` : ''));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error verificando conexión');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isPreview = config?.entorno === 'preview';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ajustes-gls-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Truck className="w-6 h-6 text-blue-600" />
          Ajustes · Integraciones Logística
        </h1>
        <p className="text-muted-foreground text-sm">
          Configuración de transportistas. Los cambios se guardan en BD (no en .env).
        </p>
      </div>

      <Tabs defaultValue="gls" className="w-full">
        <TabsList data-testid="tabs-transportistas">
          <TabsTrigger value="gls" data-testid="tab-gls">GLS</TabsTrigger>
          <TabsTrigger value="mrw" data-testid="tab-mrw">MRW</TabsTrigger>
        </TabsList>

        <TabsContent value="gls" className="space-y-6 mt-4">
          {/* Estado de la integración */}
          <Card data-testid="card-estado-integracion">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                Estado de la integración
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Entorno</Label>
                <div className="mt-1">
                  {isPreview ? (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="badge-entorno-preview">
                      <AlertTriangle className="w-3 h-3 mr-1" /> PREVIEW · mocks activos
                    </Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="badge-entorno-production">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> PRODUCCIÓN
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Último envío</Label>
                <p className="text-sm font-mono mt-1">{config?.ultimo_envio?.codbarras || '—'}</p>
                <p className="text-[10px] text-muted-foreground">{formatDT(config?.ultimo_envio?.creado_en)}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Envíos este mes</Label>
                <p className="text-2xl font-bold text-blue-700 mt-1" data-testid="stats-envios-mes">
                  {config?.stats_mes?.envios_mes || 0}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Incidencias este mes</Label>
                <p className="text-2xl font-bold text-red-700 mt-1" data-testid="stats-incidencias-mes">
                  {config?.stats_mes?.incidencias_mes || 0}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Credenciales */}
          <Card data-testid="card-credenciales">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                Credenciales
              </CardTitle>
              <CardDescription>Solo lectura por seguridad. Edítalas en el .env del servidor.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">UID cliente</Label>
                <div className="flex gap-2 items-center mt-1">
                  <code className="flex-1 px-3 py-2 bg-muted rounded font-mono text-sm tracking-wider" data-testid="uid-masked">
                    {config?.uid_cliente_masked || '(no configurado)'}
                  </code>
                  {config?.uid_cliente_set ? (
                    <Badge className="bg-green-100 text-green-800 border-green-300">✓ seteado</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 border-red-300">✗ vacío</Badge>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">URL endpoint</Label>
                <code className="block mt-1 px-3 py-2 bg-muted rounded font-mono text-xs text-foreground">
                  {config?.gls_url || '(default)'}
                </code>
              </div>
              <Button
                variant="outline"
                onClick={handleVerify}
                disabled={verifying}
                data-testid="btn-verificar-conexion"
              >
                {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Verificar conexión
              </Button>
            </CardContent>
          </Card>

          {/* Datos remitente */}
          <Card data-testid="card-remitente">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Datos del remitente</CardTitle>
              <CardDescription>
                Aparecerán en todas las etiquetas GLS. Se guardan en BD, no en .env.
                Los campos marcados "<span className="font-mono text-[10px] bg-muted px-1">env</span>" se leen del entorno cuando BD está vacía.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ['nombre', 'Nombre'],
                ['direccion', 'Dirección'],
                ['poblacion', 'Población'],
                ['provincia', 'Provincia'],
                ['cp', 'Código Postal'],
                ['telefono', 'Teléfono'],
                ['pais', 'País (prefijo)'],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label className="text-xs flex items-center gap-2">
                    {label}
                    {config?.remitente_source?.[key] && (
                      <span className="font-mono text-[9px] bg-muted px-1 rounded">
                        {config.remitente_source[key]}
                      </span>
                    )}
                  </Label>
                  <Input
                    value={remitenteDraft[key] || ''}
                    onChange={(e) => setRemitenteDraft({ ...remitenteDraft, [key]: e.target.value })}
                    data-testid={`input-remitente-${key}`}
                    className="mt-1"
                  />
                </div>
              ))}
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={loadConfig} disabled={saving}>
                  Descartar
                </Button>
                <Button onClick={handleSaveRemitente} disabled={saving} data-testid="btn-guardar-remitente">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar remitente
                </Button>
              </div>
              {config?.updated_at && (
                <p className="md:col-span-2 text-xs text-muted-foreground">
                  Última modificación: {formatDT(config.updated_at)} por {config.updated_by || '—'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Polling */}
          <Card data-testid="card-polling">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-purple-600" />
                Polling automático de tracking
              </CardTitle>
              <CardDescription>
                Intervalo en horas con el que el scheduler consulta GLS por cada envío activo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end gap-3">
                <div className="w-32">
                  <Label className="text-xs">Intervalo (horas)</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="48"
                    value={pollingDraft}
                    onChange={(e) => setPollingDraft(e.target.value)}
                    data-testid="input-polling-hours"
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleSavePolling} disabled={saving} data-testid="btn-guardar-polling">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
                <Separator orientation="vertical" className="h-10" />
                <Button
                  variant="outline"
                  onClick={handleForcePoll}
                  disabled={forcing}
                  data-testid="btn-force-poll"
                >
                  {forcing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Forzar actualización ahora
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Actual: {config?.polling_hours}h. Los cambios requieren reinicio del backend para aplicar al scheduler (
                el botón <strong>Forzar actualización</strong> ejecuta un tick inmediato sin reiniciar).
              </p>
            </CardContent>
          </Card>

          {/* Servicio y horario por defecto */}
          <Card data-testid="card-servicio-defecto">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-4 h-4 text-blue-600" />
                Servicio y horario por defecto
              </CardTitle>
              <CardDescription>
                Valores que se usarán al crear nuevas etiquetas si el operador no elige otros.
                Puedes cambiarlos individualmente al crear cada envío.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label className="text-xs">Servicio por defecto</Label>
                  <select
                    value={servicioDraft}
                    onChange={(e) => setServicioDraft(e.target.value)}
                    data-testid="select-servicio-defecto"
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                    {(config?.servicios_catalogo || []).map((s) => (
                      <option key={s.codigo} value={s.codigo}>
                        {s.nombre} {s.descripcion ? `· ${s.descripcion}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Horario por defecto</Label>
                  <select
                    value={horarioDraft}
                    onChange={(e) => setHorarioDraft(e.target.value)}
                    data-testid="select-horario-defecto"
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                    {(config?.horarios_catalogo || []).map((h) => (
                      <option key={h.codigo} value={h.codigo}>
                        {h.nombre} {h.descripcion ? `· ${h.descripcion}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <Button onClick={handleSaveServicio} disabled={saving} data-testid="btn-guardar-servicio">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Actual: <strong>{config?.servicios_catalogo?.find((s) => s.codigo === config?.servicio_defecto)?.nombre || config?.servicio_defecto}</strong>
                {' · '}
                <strong>{config?.horarios_catalogo?.find((h) => h.codigo === config?.horario_defecto)?.nombre || config?.horario_defecto}</strong>.
              </p>
            </CardContent>
          </Card>

          {/* Vinculación manual por codexp/codbarras */}
          <VincularEnvioManualCard />

          {/* Importador CSV en lote: numero_autorizacion,codexp */}
          <ImportadorCSVCard />

          {/* Sync histórico por codigo_recogida_salida */}
          <SyncRecogidaSalidaCard />

          {/* Sincronización histórica */}
          <SincronizacionHistoricaCard />
        </TabsContent>

        <TabsContent value="mrw">
          <MRWConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Sincronización de órdenes históricas con GLS
// ──────────────────────────────────────────────────────────────────────────────

function VincularEnvioManualCard() {
  const [codigo, setCodigo] = useState('');
  const [ordenId, setOrdenId] = useState('');
  const [diagnosing, setDiagnosing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [diag, setDiag] = useState(null);
  const [linkResult, setLinkResult] = useState(null);

  const handleDiagnose = async () => {
    const c = codigo.trim();
    if (!c) {
      toast.error('Pon un codexp o codbarras');
      return;
    }
    setDiagnosing(true);
    setDiag(null);
    setLinkResult(null);
    try {
      const r = await API.get('/api/logistica/gls/diagnostico-refc', { params: { referencia: c } });
      setDiag(r.data);
      if (r.data?.campos_extraidos?.success) {
        toast.success('GLS encontró la expedición');
      } else {
        toast.warning('GLS no encuentra esa expedición');
      }
    } catch (e) {
      toast.error('Error consultando GLS');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleLink = async () => {
    const c = codigo.trim();
    if (!c) return;
    setLinking(true);
    setLinkResult(null);
    try {
      const payload = { codigo: c, dry_run: false };
      if (ordenId.trim()) payload.orden_id = ordenId.trim();
      const r = await API.post('/api/logistica/gls/vincular-por-codigo', payload);
      setLinkResult(r.data);
      if (r.data?.ok) {
        toast.success(`Envío ${r.data.action} en orden ${r.data.orden_match?.numero_orden}`);
      } else {
        toast.warning(r.data?.mensaje || 'No se pudo vincular automáticamente');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error vinculando';
      toast.error(msg);
    } finally {
      setLinking(false);
    }
  };

  const ce = diag?.campos_extraidos || {};

  return (
    <Card data-testid="card-vincular-envio-manual">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="w-4 h-4 text-indigo-600" />
          Vincular envío manualmente (codexp / codbarras)
        </CardTitle>
        <CardDescription>
          Si el envío se creó desde la <strong>extranet GLS</strong> (no desde el CRM), GLS no permite buscarlo por
          referencia. Pega aquí el <code className="font-mono text-xs bg-muted px-1">codexp</code> que aparece en
          la URL de la extranet (ej: <code className="font-mono text-xs bg-muted px-1">1288560505</code>) y
          consultaremos GLS, extraeremos la referencia de "Observaciones" y lo enlazaremos a la orden correcta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">codexp o codbarras *</Label>
            <Input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="1288560505"
              className="mt-1 font-mono"
              data-testid="input-vincular-codigo"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDiagnose}
              disabled={diagnosing || !codigo.trim()}
              data-testid="btn-vincular-diagnose"
            >
              {diagnosing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Consultar GLS
            </Button>
          </div>
        </div>

        {diag && (
          <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm" data-testid="diag-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <p className="text-xs text-muted-foreground">success</p>
                <p className="font-mono">{String(ce.success)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">codexp</p>
                <p className="font-mono">{ce.codexp || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">codbarras</p>
                <p className="font-mono">{ce.codbarras_real || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">CP destino</p>
                <p className="font-mono">{ce.cp_destino || '—'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-muted-foreground">Referencia (de &lt;Observacion&gt;)</p>
                <p className="font-mono font-bold text-indigo-700">{ce.refc_devuelta || '—'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-muted-foreground">Estado actual</p>
                <p className="font-mono">{ce.estado_actual || '—'}</p>
              </div>
            </div>
            {diag.url_tracking_construida && (
              <div>
                <p className="text-xs text-muted-foreground">URL tracking</p>
                <a
                  href={diag.url_tracking_construida}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-xs text-blue-700 underline break-all"
                  data-testid="diag-tracking-url"
                >
                  {diag.url_tracking_construida}
                </a>
              </div>
            )}
            {diag.match_bd_por_numero_autorizacion ? (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Match BD: {diag.match_bd_por_numero_autorizacion.numero_orden}
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Sin match automático en BD — pega `orden_id` abajo para vincular manualmente
              </Badge>
            )}
          </div>
        )}

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">orden_id (opcional, vincular manualmente si no hay match)</Label>
            <Input
              value={ordenId}
              onChange={(e) => setOrdenId(e.target.value)}
              placeholder="d548c633-1dc4-... (UUID de la orden)"
              className="mt-1 font-mono text-xs"
              data-testid="input-vincular-orden-id"
            />
          </div>
          <Button
            onClick={handleLink}
            disabled={linking || !codigo.trim() || !diag?.campos_extraidos?.success}
            data-testid="btn-vincular-link"
          >
            {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Vincular envío
          </Button>
        </div>

        {linkResult && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              linkResult.ok ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
            }`}
            data-testid="link-result"
          >
            <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(linkResult, null, 2)}</pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ──────────────────────────────────────────────────────────────────────────────
// Importador CSV en lote: numero_autorizacion,codexp
// ──────────────────────────────────────────────────────────────────────────────

function ImportadorCSVCard() {
  const [csv, setCsv] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const parseCSV = (text) => {
    const out = [];
    text.split(/\r?\n/).forEach((raw) => {
      const ln = raw.trim();
      if (!ln || ln.startsWith('#')) return;
      const parts = ln.split(/[,;\t]/).map((s) => s.trim());
      if (parts.length < 2) return;
      const [na, ce] = parts;
      if (na && ce) out.push({ numero_autorizacion: na, codexp: ce });
    });
    return out;
  };

  const lineas = parseCSV(csv);

  const handleSubmit = async () => {
    if (lineas.length === 0) {
      toast.error('No hay líneas válidas (formato: numero_autorizacion,codexp)');
      return;
    }
    if (lineas.length > 500) {
      toast.error('Máximo 500 líneas por lote.');
      return;
    }
    setLoading(true);
    setResultado(null);
    try {
      const r = await API.post('/api/logistica/gls/vincular-bulk-csv', {
        lineas,
        dry_run: dryRun,
      });
      setResultado(r.data);
      if (dryRun) {
        toast.success(`Dry-run: ${r.data.total_lineas} líneas analizadas`);
      } else {
        toast.success(`${r.data.vinculadas}/${r.data.total_lineas} envíos vinculados`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error procesando CSV';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="card-importador-csv">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-violet-600" />
          Importador CSV en lote
        </CardTitle>
        <CardDescription>
          Pega un CSV con formato <code className="font-mono text-xs bg-muted px-1">numero_autorizacion,codexp</code>{' '}
          (una línea por orden). Se admiten separadores coma, punto-y-coma o tabulador. Las líneas que empiezan por
          <code className="font-mono text-xs bg-muted px-1">#</code> se ignoran. Máximo 500 líneas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={8}
          placeholder={`# numero_autorizacion,codexp\n26BE002120,1288560505\n26BE003344,1288601234`}
          className="w-full font-mono text-xs rounded-md border border-slate-300 p-3 bg-card"
          data-testid="textarea-csv"
        />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-muted-foreground">
            Líneas válidas detectadas: <span className="font-bold text-slate-800">{lineas.length}</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                data-testid="check-csv-dryrun"
              />
              Dry-run (simular sin escribir)
            </label>
            <Button
              onClick={handleSubmit}
              disabled={loading || lineas.length === 0}
              data-testid="btn-csv-importar"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {dryRun ? 'Simular' : 'Importar'}
            </Button>
          </div>
        </div>

        {resultado && (
          <div className="rounded-lg border p-3 bg-muted/50" data-testid="csv-resultado">
            <p className="text-xs text-muted-foreground mb-2">
              {resultado.dry_run ? 'Resultado dry-run' : 'Resultado'}: {resultado.vinculadas}/{resultado.total_lineas} ok
            </p>
            <div className="max-h-80 overflow-y-auto rounded border bg-card">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">numero_autorizacion</th>
                    <th className="text-left px-2 py-1">codexp</th>
                    <th className="text-left px-2 py-1">orden</th>
                    <th className="text-left px-2 py-1">status</th>
                    <th className="text-left px-2 py-1">detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {(resultado.resultados || []).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1 font-mono">{r.numero_autorizacion}</td>
                      <td className="px-2 py-1 font-mono">{r.codexp}</td>
                      <td className="px-2 py-1">{r.numero_orden || '—'}</td>
                      <td className="px-2 py-1">
                        {r.status === 'ok' ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300">{r.action || 'ok'}</Badge>
                        ) : r.status === 'would_process' ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">would_process</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 border-red-300">{r.status}</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-foreground/70">
                        {r.tracking_url ? (
                          <a href={r.tracking_url} target="_blank" rel="noreferrer" className="text-blue-700 underline">
                            tracking
                          </a>
                        ) : (
                          r.reason || ''
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ──────────────────────────────────────────────────────────────────────────────
// Sync histórico por codigo_recogida_salida (órdenes de la extranet GLS)
// ──────────────────────────────────────────────────────────────────────────────

function SyncRecogidaSalidaCard() {
  const [dryRun, setDryRun] = useState(true);
  const [maxOrdenes, setMaxOrdenes] = useState(50);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);
    setResultado(null);
    try {
      const r = await API.post('/api/logistica/gls/vincular-historico-por-recogida-salida', {
        dry_run: dryRun,
        max_ordenes: Number(maxOrdenes) || 50,
        incluir_ya_vinculadas: false,
      });
      setResultado(r.data);
      if (dryRun) {
        toast.success(`Dry-run: ${r.data.candidatas_total} candidatas analizadas`);
      } else {
        toast.success(`${r.data.vinculadas}/${r.data.candidatas_total} envíos vinculados`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error en sync';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="card-sync-recogida-salida">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-cyan-600" />
          Sync histórico por <code className="font-mono text-xs bg-muted px-1">codigo_recogida_salida</code>
        </CardTitle>
        <CardDescription>
          Recorre órdenes con <code className="font-mono text-xs bg-muted px-1">codigo_recogida_salida</code> relleno (10–14 dígitos)
          y agencia GLS o vacía, sin envío vinculado. Para cada una llama GLS, extrae codexp/cp_destino y construye la URL
          canónica de tracking. Idempotente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-32">
            <Label className="text-xs">Máx órdenes</Label>
            <Input
              type="number" min={1} max={500}
              value={maxOrdenes}
              onChange={(e) => setMaxOrdenes(e.target.value)}
              className="mt-1"
              data-testid="input-sync-rs-max"
            />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer pb-2">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              data-testid="check-sync-rs-dryrun"
            />
            Dry-run
          </label>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            data-testid="btn-sync-rs-run"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {dryRun ? 'Simular' : 'Ejecutar'}
          </Button>
        </div>

        {resultado && (
          <div className="rounded-lg border p-3 bg-muted/50" data-testid="sync-rs-resultado">
            <p className="text-xs text-muted-foreground mb-2">
              Candidatas: <span className="font-bold">{resultado.candidatas_total}</span> · Vinculadas:{' '}
              <span className="font-bold text-green-700">{resultado.vinculadas}</span>
              {resultado.dry_run && ' · (dry-run)'}
            </p>
            <div className="max-h-64 overflow-y-auto rounded border bg-card">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">orden</th>
                    <th className="text-left px-2 py-1">código</th>
                    <th className="text-left px-2 py-1">status</th>
                    <th className="text-left px-2 py-1">detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {(resultado.resultados || []).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{r.numero_orden || '—'}</td>
                      <td className="px-2 py-1 font-mono">{r.codigo}</td>
                      <td className="px-2 py-1">
                        {r.status === 'ok' ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300">{r.action || 'ok'}</Badge>
                        ) : r.status === 'would_process' ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">would_process</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300">{r.status}</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-foreground/70">
                        {r.tracking_url ? (
                          <a href={r.tracking_url} target="_blank" rel="noreferrer" className="text-blue-700 underline">
                            tracking
                          </a>
                        ) : (
                          r.reason || ''
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



function SincronizacionHistoricaCard() {
  const [candidatas, setCandidatas] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diasAtras, setDiasAtras] = useState(45);
  // Salvaguardas production:
  const [dryRun, setDryRun] = useState(true);
  const [maxOrdenes, setMaxOrdenes] = useState(50);
  const [confirmacion, setConfirmacion] = useState('');
  const [forzarWarning, setForzarWarning] = useState(false);
  // Histórico runs:
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);

  const entorno = candidatas?.entorno || 'production';
  const isProduction = entorno === 'production';
  const softWarning = candidatas?.soft_warning_max_ordenes || 50;
  const hardCap = candidatas?.hard_cap_max_ordenes || 500;
  const textoConfirmacion = candidatas?.confirmacion_texto || 'CONFIRMO';

  const loadCandidatas = async () => {
    try {
      const { data } = await API.get(`/logistica/gls/sincronizar-ordenes/candidatas?dias_atras=${diasAtras}`);
      setCandidatas(data);
    } catch { /* silencioso */ }
  };

  const loadRuns = async () => {
    try {
      const { data } = await API.get('/logistica/gls/sync-runs?limit=20');
      setRuns(data.runs || []);
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    loadCandidatas();
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diasAtras]);

  // Validaciones UI (antes de llamar al backend)
  const realRun = !dryRun && isProduction;
  const exigeConfirmacion = realRun && maxOrdenes > 20;
  const confirmacionValida = !exigeConfirmacion || confirmacion === textoConfirmacion;
  const superaWarning = maxOrdenes > softWarning;
  const warningValido = !realRun || !superaWarning || forzarWarning;
  const puedeEjecutar = !loading && candidatas?.total_candidatas > 0 &&
                        confirmacionValida && warningValido &&
                        maxOrdenes > 0 && maxOrdenes <= hardCap;

  // Estado/handler para limpiar envíos mock_preview tras paso a producción
  const [limpiando, setLimpiando] = useState(false);
  const [resumenLimpieza, setResumenLimpieza] = useState(null);

  const limpiarMocks = async (dry) => {
    setLimpiando(true);
    setResumenLimpieza(null);
    try {
      const { data } = await API.post(`/logistica/gls/limpiar-mocks?dry_run=${dry ? 'true' : 'false'}`);
      setResumenLimpieza(data);
      if (dry) {
        toast.info(
          `DRY-RUN: ${data.ordenes_afectadas?.gls || 0} GLS + ${data.ordenes_afectadas?.mrw || 0} MRW se borrarían`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `Limpiado: ${data.ordenes_gls_modificadas} GLS + ${data.ordenes_mrw_modificadas} MRW + ${data.gls_shipments_eliminados} shipments`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al limpiar mocks');
    } finally {
      setLimpiando(false);
    }
  };

  const ejecutarSync = async () => {    if (realRun && !window.confirm(
      `⚠️ MODO PRODUCCIÓN REAL ⚠️\n\n` +
      `Vas a sincronizar hasta ${maxOrdenes} órdenes contra GLS REAL y\n` +
      `modificar MongoDB real.\n\n` +
      `Se creará un BACKUP automático antes de cada escritura.\n` +
      `Podrás revertir vía el botón "Restaurar" en el histórico de runs.\n\n` +
      `¿Continuar?`,
    )) return;
    if (dryRun && !window.confirm(
      `Ejecutar DRY-RUN (simulación, no modifica BD).\n` +
      `Útil para verificar qué se sincronizará antes del run real.\n\n¿Continuar?`,
    )) return;

    setLoading(true);
    try {
      const { data } = await API.post('/logistica/gls/sincronizar-ordenes', {
        solo_sin_envios: true,
        dias_atras: diasAtras,
        max_ordenes: maxOrdenes,
        dry_run: dryRun,
        confirmacion: exigeConfirmacion ? confirmacion : '',
        forzar_por_encima_del_warning: forzarWarning,
      });
      setResultados(data);
      const etiqueta = data.dry_run
        ? 'DRY-RUN'
        : (data.preview ? 'PREVIEW' : 'REAL');
      toast.success(
        `Sync [${etiqueta}] completado. ${data.sincronizadas} ok, ` +
        `${data.no_encontradas} no encontradas, ${data.con_error} errores. ` +
        `run: ${data.sync_run_id.slice(-12)}`,
      );
      setConfirmacion('');
      loadCandidatas();
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error ejecutando sync');
    } finally {
      setLoading(false);
    }
  };

  const restaurarRun = async (runId) => {
    const texto = isProduction
      ? `Para restaurar el run ${runId.slice(-12)} en PRODUCCIÓN,\n` +
        `escribe ${textoConfirmacion} y pulsa OK:`
      : `Restaurar run ${runId.slice(-12)} (preview). Pulsa OK.`;
    const resp = window.prompt(texto, isProduction ? '' : textoConfirmacion);
    if (resp === null) return;
    try {
      const { data } = await API.post(
        `/logistica/gls/sync-runs/${runId}/restaurar`,
        { confirmacion: resp },
      );
      toast.success(`Restauradas ${data.restauradas} órdenes del run ${runId.slice(-12)}`);
      loadRuns();
      loadCandidatas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restaurar');
    }
  };

  const statusBadge = (status) => {
    if (status === 'ok') return <Badge className="bg-green-100 text-green-800 border-green-300 text-[10px]">OK</Badge>;
    if (status === 'not_found') return <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">No encontrada</Badge>;
    if (status === 'skipped') return <Badge className="bg-muted text-foreground border-slate-300 text-[10px]">Skip</Badge>;
    return <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px]">Error</Badge>;
  };

  return (
    <Card data-testid="card-sync-historico">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-blue-600" />
          Sincronización de órdenes históricas
          <Badge
            className={isProduction
              ? "bg-red-100 text-red-800 border-red-300 ml-2"
              : "bg-amber-100 text-amber-800 border-amber-300 ml-2"}
            data-testid="badge-entorno"
          >
            {entorno === 'preview' ? 'PREVIEW (mock)' : 'PRODUCTION (real)'}
          </Badge>
        </CardTitle>
        <CardDescription>
          Vincula órdenes antiguas con envíos GLS creados desde la extranet
          (usando <code>numero_autorizacion</code> como RefC). Idempotente: no
          modifica otros campos. Cada ejecución real genera backup automático.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Aviso production */}
        {isProduction && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-xs flex gap-2"
               data-testid="aviso-production">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">
                Entorno PRODUCTION activo
              </p>
              <p className="text-red-800">
                Las ejecuciones no dry-run tocarán GLS real y MongoDB real.
                Se creará un backup por orden antes de cualquier escritura.
              </p>
            </div>
          </div>
        )}

        {/* Limpieza one-shot de envíos preview/mock que quedaron en BD */}
        {isProduction && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2"
               data-testid="bloque-limpieza-mocks">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  Limpieza de envíos preview (one-shot)
                </p>
                <p className="text-xs text-amber-800">
                  Borra de la BD los envíos GLS/MRW marcados como <code>mock_preview</code> que se generaron mientras
                  el sistema estuvo en modo preview. Idempotente: ejecutarlo varias veces no rompe nada.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={limpiando}
                onClick={() => limpiarMocks(true)}
                data-testid="btn-limpiar-mocks-dry"
              >
                {limpiando ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Simular (dry-run)
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={limpiando}
                onClick={() => {
                  if (window.confirm('¿Borrar definitivamente los envíos mock_preview de la BD? (acción auditada)')) {
                    limpiarMocks(false);
                  }
                }}
                data-testid="btn-limpiar-mocks-real"
              >
                {limpiando ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Limpiar definitivamente
              </Button>
              {resumenLimpieza && (
                <span className="text-xs self-center text-foreground bg-card border rounded px-2 py-1">
                  {resumenLimpieza.dry_run
                    ? `Dry-run: ${resumenLimpieza.ordenes_afectadas?.gls || 0} GLS + ${resumenLimpieza.ordenes_afectadas?.mrw || 0} MRW se borrarían`
                    : `OK: ${resumenLimpieza.ordenes_gls_modificadas} GLS + ${resumenLimpieza.ordenes_mrw_modificadas} MRW + ${resumenLimpieza.gls_shipments_eliminados} shipments`}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Parámetros */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Ventana (días)</Label>
            <Input
              type="number" min="1" max="365"
              value={diasAtras}
              onChange={(e) => setDiasAtras(Number(e.target.value) || 45)}
              data-testid="input-sync-dias"
            />
          </div>
          <div>
            <Label className="text-xs">Máx órdenes</Label>
            <Input
              type="number" min="1" max={hardCap}
              value={maxOrdenes}
              onChange={(e) => setMaxOrdenes(Number(e.target.value) || 1)}
              data-testid="input-sync-max-ordenes"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Soft: {softWarning} · Hard cap: {hardCap}
            </p>
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label className="text-xs">Candidatas (sin gls_envios)</Label>
            <p className="text-2xl font-bold text-blue-700" data-testid="sync-total-candidatas">
              {candidatas?.total_candidatas ?? '—'}
            </p>
          </div>
          <div className="col-span-2 md:col-span-1 flex items-end">
            <Button
              onClick={ejecutarSync}
              disabled={!puedeEjecutar}
              className={realRun ? "bg-red-600 hover:bg-red-700 w-full" : "w-full"}
              data-testid="btn-ejecutar-sync"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              {dryRun ? 'Simular (dry-run)' : 'Ejecutar REAL'}
            </Button>
          </div>
        </div>

        {/* Modo: dry-run vs real */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-muted/50 rounded-lg p-3 border">
          <label className="flex items-center gap-2 text-sm cursor-pointer"
                 data-testid="checkbox-dry-run-label">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => { setDryRun(e.target.checked); setConfirmacion(''); }}
              data-testid="checkbox-dry-run"
              className="w-4 h-4"
            />
            <span className="font-medium">Modo simulación (dry-run)</span>
            <span className="text-xs text-muted-foreground">
              — no toca BD, solo muestra qué haría
            </span>
          </label>
        </div>

        {/* Confirmación de texto (solo en run real production con > 20 órdenes) */}
        {exigeConfirmacion && (
          <div className="bg-amber-50 border border-amber-400 rounded-lg p-3 space-y-2"
               data-testid="panel-confirmacion-real">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>Lote grande ({maxOrdenes} órdenes).</strong> Escribe <code>{textoConfirmacion}</code> para habilitar el botón:
              </p>
            </div>
            <Input
              placeholder={`Escribe "${textoConfirmacion}"`}
              value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)}
              data-testid="input-confirmacion-texto"
              className={confirmacion === textoConfirmacion
                ? "border-green-500" : "border-amber-400"}
            />
            {superaWarning && (
              <label className="flex items-center gap-2 text-xs cursor-pointer"
                     data-testid="checkbox-forzar-warning-label">
                <input
                  type="checkbox"
                  checked={forzarWarning}
                  onChange={(e) => setForzarWarning(e.target.checked)}
                  data-testid="checkbox-forzar-warning"
                />
                Confirmo procesar más de {softWarning} órdenes ({maxOrdenes}).
              </label>
            )}
          </div>
        )}

        {/* Muestra */}
        {candidatas?.muestra?.length > 0 && !resultados && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Ver muestra de las 10 más recientes
            </summary>
            <div className="mt-2 space-y-1">
              {candidatas.muestra.map((o) => (
                <div key={o.id} className="flex justify-between border-b py-1 text-xs">
                  <span className="font-mono">{o.numero_orden}</span>
                  <span className="font-mono text-blue-700">{o.numero_autorizacion}</span>
                  <span className="text-muted-foreground">{o.cp_envio || '—'}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Resultados */}
        {resultados && (
          <div className="space-y-3" data-testid="sync-resultados">
            <div className="flex flex-wrap gap-2 items-center">
              <Badge
                className={resultados.dry_run
                  ? "bg-muted text-slate-800 border-slate-300"
                  : (resultados.preview
                     ? "bg-amber-100 text-amber-800 border-amber-300"
                     : "bg-red-100 text-red-800 border-red-300")}
                data-testid="badge-resultado-modo"
              >
                {resultados.dry_run ? 'DRY-RUN' : (resultados.preview ? 'PREVIEW' : 'REAL')}
              </Badge>
              <span className="text-xs text-muted-foreground font-mono"
                    data-testid="texto-sync-run-id">
                run_id: {resultados.sync_run_id}
              </span>
              {!resultados.dry_run && !resultados.preview && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => restaurarRun(resultados.sync_run_id)}
                  data-testid="btn-restaurar-run-actual"
                >
                  Restaurar este run
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-xs">
              <div className="bg-green-50 rounded p-2">
                <p className="text-xl font-bold text-green-700">{resultados.sincronizadas}</p>
                <p className="text-[10px]">Sincronizadas</p>
              </div>
              <div className="bg-blue-50 rounded p-2">
                <p className="text-xl font-bold text-blue-700">{resultados.creadas}</p>
                <p className="text-[10px]">Creadas</p>
              </div>
              <div className="bg-indigo-50 rounded p-2">
                <p className="text-xl font-bold text-indigo-700">{resultados.actualizadas}</p>
                <p className="text-[10px]">Actualizadas</p>
              </div>
              <div className="bg-amber-50 rounded p-2">
                <p className="text-xl font-bold text-amber-700">{resultados.no_encontradas}</p>
                <p className="text-[10px]">No encontradas</p>
              </div>
              <div className="bg-red-50 rounded p-2">
                <p className="text-xl font-bold text-red-700">{resultados.con_error}</p>
                <p className="text-[10px]">Con error</p>
              </div>
            </div>

            {resultados.warnings?.length > 0 && (
              <div className="bg-muted/50 border rounded p-2 text-xs space-y-1"
                   data-testid="sync-warnings">
                {resultados.warnings.map((w, i) => (
                  <p key={i} className="text-foreground">• {w}</p>
                ))}
              </div>
            )}

            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-left">OT</th>
                    <th className="p-2 text-left">Autorización</th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-left">Codbarras</th>
                    <th className="p-2 text-left">CP</th>
                    <th className="p-2 text-left">Tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.resultados.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50" data-testid={`sync-row-${r.order_id}`}>
                      <td className="p-2 font-mono">{r.numero_orden || '—'}</td>
                      <td className="p-2 font-mono text-blue-700">{r.numero_autorizacion}</td>
                      <td className="p-2">{statusBadge(r.status)}{r.status !== 'ok' && r.status !== 'ok_dryrun' && r.reason && <span className="text-[10px] text-muted-foreground ml-1">({r.reason})</span>}</td>
                      <td className="p-2 font-mono">{r.codbarras || '—'}</td>
                      <td className="p-2">{r.cp_destinatario || '—'}</td>
                      <td className="p-2">
                        {r.tracking_url ? (
                          <a href={r.tracking_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                            abrir
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {resultados.no_encontradas > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs">
                <p className="font-medium text-amber-900 mb-1">
                  {resultados.no_encontradas} órdenes no encontradas en GLS.
                </p>
                <p className="text-amber-700">
                  Verifica que el `numero_autorizacion` corresponde al `RefC` enviado a GLS.
                  Posibles causas: envío nunca creado en GLS, refC erróneo, o envío muy antiguo borrado del histórico GLS.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Histórico de runs */}
        <div className="border-t pt-3">
          <button
            onClick={() => { setShowRuns(!showRuns); if (!showRuns) loadRuns(); }}
            className="text-xs text-blue-700 hover:underline"
            data-testid="btn-toggle-runs"
          >
            {showRuns ? '▼' : '▶'} Histórico de ejecuciones ({runs.length})
          </button>
          {showRuns && runs.length > 0 && (
            <div className="mt-2 max-h-64 overflow-y-auto border rounded-lg"
                 data-testid="tabla-historico-runs">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Modo</th>
                    <th className="p-2 text-left">Actor</th>
                    <th className="p-2 text-center">OK</th>
                    <th className="p-2 text-center">Err</th>
                    <th className="p-2 text-left">Run ID</th>
                    <th className="p-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.sync_run_id} className="border-b hover:bg-muted/50"
                        data-testid={`run-row-${r.sync_run_id}`}>
                      <td className="p-2">{formatDT(r.ejecutado_en)}</td>
                      <td className="p-2">
                        <Badge className={r.dry_run
                          ? "bg-muted text-foreground border-slate-300 text-[10px]"
                          : (r.preview
                             ? "bg-amber-100 text-amber-800 border-amber-300 text-[10px]"
                             : "bg-red-100 text-red-800 border-red-300 text-[10px]")}>
                          {r.dry_run ? 'DRY' : (r.preview ? 'PREV' : 'REAL')}
                        </Badge>
                        {r.restaurado && (
                          <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 text-[10px] ml-1">
                            RESTAURADO
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 text-[10px]">{r.actor || '—'}</td>
                      <td className="p-2 text-center text-green-700 font-semibold">
                        {r.stats?.sincronizadas ?? 0}
                      </td>
                      <td className="p-2 text-center text-red-700">
                        {r.stats?.con_error ?? 0}
                      </td>
                      <td className="p-2 font-mono text-[10px] text-muted-foreground">
                        {r.sync_run_id.slice(-16)}
                      </td>
                      <td className="p-2 text-center">
                        {!r.dry_run && !r.restaurado && (
                          <Button
                            size="sm" variant="outline"
                            className="h-6 text-[10px] px-2"
                            onClick={() => restaurarRun(r.sync_run_id)}
                            data-testid={`btn-restaurar-${r.sync_run_id}`}
                          >
                            Restaurar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {showRuns && runs.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">Sin ejecuciones previas.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MRW Config Tab (mismo patrón que GLS)
// ──────────────────────────────────────────────────────────────────────────────

function MRWConfigTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [remitenteDraft, setRemitenteDraft] = useState({
    nombre: '', direccion: '', poblacion: '', provincia: '', cp: '', telefono: '',
  });

  useEffect(() => {
    loadMRW();
  }, []);

  const loadMRW = async () => {
    try {
      const { data } = await API.get('/logistica/config/mrw');
      setConfig(data);
      setRemitenteDraft(data.remitente);
    } catch {
      toast.error('Error cargando configuración MRW');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRemitente = async () => {
    setSaving(true);
    try {
      const { data } = await API.post('/logistica/config/mrw/remitente', remitenteDraft);
      setConfig(data);
      setRemitenteDraft(data.remitente);
      toast.success('Remitente MRW guardado en BD');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando remitente MRW');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await API.post('/logistica/config/mrw/verify');
      if (data.ok) toast.success(data.mensaje);
      else toast.error(data.mensaje);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error verificando MRW');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const isPreview = config?.entorno === 'preview';

  return (
    <div className="space-y-6 mt-4" data-testid="mrw-config-content">
      {/* Estado */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-600" /> Estado integración MRW
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Entorno</Label>
            <div className="mt-1">
              {isPreview ? (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="mrw-badge-preview">
                  <AlertTriangle className="w-3 h-3 mr-1" /> PREVIEW · mocks activos
                </Badge>
              ) : (
                <Badge className="bg-green-100 text-green-800 border-green-300">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> PRODUCCIÓN
                </Badge>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Último envío</Label>
            <p className="text-sm font-mono mt-1">{config?.ultimo_envio?.num_envio || '—'}</p>
            <p className="text-[10px] text-muted-foreground">{config?.ultimo_envio?.creado_en ? new Date(config.ultimo_envio.creado_en).toLocaleString('es-ES') : '—'}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Envíos este mes</Label>
            <p className="text-2xl font-bold text-blue-700 mt-1">{config?.stats_mes?.envios_mes || 0}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Recogidas pendientes</Label>
            <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="mrw-recogidas-pendientes">
              {config?.stats_mes?.recogidas_pendientes || 0}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Credenciales */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-600" /> Credenciales
          </CardTitle>
          <CardDescription>Solo lectura. Edita en el .env del servidor.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Franquicia</Label>
            <code className="block mt-1 px-2 py-2 bg-muted rounded font-mono text-sm tracking-wider" data-testid="mrw-franquicia">
              {config?.franquicia_masked || '(no configurado)'}
            </code>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Abonado</Label>
            <code className="block mt-1 px-2 py-2 bg-muted rounded font-mono text-sm tracking-wider">
              {config?.abonado_masked || '—'}
            </code>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Usuario</Label>
            <code className="block mt-1 px-2 py-2 bg-muted rounded font-mono text-sm tracking-wider">
              {config?.usuario_masked || '—'}
            </code>
          </div>
          <div className="md:col-span-3">
            <Button variant="outline" onClick={handleVerify} disabled={verifying} data-testid="btn-mrw-verify">
              {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Verificar conexión
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Remitente */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Datos del remitente</CardTitle>
          <CardDescription>Aparecerán en etiquetas MRW. Se guardan en BD (colección `configuracion` tipo=mrw).</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['nombre', 'Nombre'],
            ['direccion', 'Dirección'],
            ['poblacion', 'Población'],
            ['provincia', 'Provincia'],
            ['cp', 'CP'],
            ['telefono', 'Teléfono'],
          ].map(([key, label]) => (
            <div key={key}>
              <Label className="text-xs flex items-center gap-2">
                {label}
                {config?.remitente_source?.[key] && (
                  <span className="font-mono text-[9px] bg-muted px-1 rounded">
                    {config.remitente_source[key]}
                  </span>
                )}
              </Label>
              <Input
                value={remitenteDraft[key] || ''}
                onChange={(e) => setRemitenteDraft({ ...remitenteDraft, [key]: e.target.value })}
                data-testid={`input-mrw-remitente-${key}`}
                className="mt-1"
              />
            </div>
          ))}
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button variant="outline" onClick={loadMRW} disabled={saving}>Descartar</Button>
            <Button onClick={handleSaveRemitente} disabled={saving} data-testid="btn-mrw-guardar-remitente">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar remitente
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
